import { useCallback } from "react";

import { useMutation } from "@apollo/client";

import { graphql } from "@/gql";
import { CreateMealInput } from "@/gql/graphql";
import { extractStoreFieldArgs } from "@/utils/apollo";

const newMealFragment = graphql(`
  fragment NewMeal on Meal {
    ...MealTab
    ...MealDetails
    ...MealComments
    consumedAt
  }
`);

const createMealMutation = graphql(`
  mutation CreateMeal($input: CreateMealInput!) {
    createMeal(input: $input) {
      meal {
        ...NewMeal
      }
    }
  }
`);

export function useCreateMeal() {
  const [createMeal] = useMutation(createMealMutation);

  return useCallback(
    async (input: CreateMealInput) => {
      const result = await createMeal({
        variables: { input },
        update(cache, { data }) {
          const newMealRef = cache.writeFragment({
            data: data?.createMeal.meal,
            fragment: newMealFragment,
            fragmentName: "NewMeal",
          });
          cache.modify({
            fields: {
              meals(existingMealsRef, { storeFieldName }) {
                const args = extractStoreFieldArgs(storeFieldName);
                if (
                  args.clientId !== input.clientId ||
                  args.consumedOn !== input.consumedOn
                ) {
                  return existingMealsRef;
                }

                return {
                  ...existingMealsRef,
                  nodes: existingMealsRef.nodes.concat(newMealRef),
                };
              },
            },
          });
        },
      });

      return result.data!.createMeal.meal;
    },
    [createMeal]
  );
}
